import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';

interface ExpiredLicense {
  id: string;
  licenseId: string;
  trackId: string;
  trackName?: string;
  sourceOrgId: string;
  sourceOrgName?: string;
  licenseType: string;
  dateExpires?: number;
  deactivatedAt?: number;
  licensePath: string;
}

interface ExpiredLicensesModalProps {
  firebase: any;
}

const ExpiredLicensesModal: React.FC<ExpiredLicensesModalProps> = ({ firebase }) => {
  const [expiredLicenses, setExpiredLicenses] = useState<ExpiredLicense[]>([]);
  const [loading, setLoading] = useState(false);
  const [acknowledgingIds, setAcknowledgingIds] = useState<Set<string>>(new Set());
  const [acknowledgingAll, setAcknowledgingAll] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  // Use notifications store for modal visibility and counts
  const isOpen = useNotificationsStore(state => state.showExpiredLicensesModal);
  const setIsOpen = useNotificationsStore(state => state.setShowExpiredLicensesModal);
  const setExpiredLicensesCount = useNotificationsStore(state => state.setExpiredLicensesCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Check for expired licenses when org changes
  useEffect(() => {
    const checkExpiredLicenses = async () => {
      if (!currentOrgId || !firebase) {
        setExpiredLicenses([]);
        setExpiredLicensesCount(0);
        return;
      }

      setLoading(true);
      try {
        // Fetch both expired and deactivated licenses
        const [expired, deactivated] = await Promise.all([
          firebase.getExpiredLicensesForClient(currentOrgId),
          firebase.getDeactivatedLicensesForClient(currentOrgId),
        ]);

        // Combine and deduplicate
        const allExpired = [...expired, ...deactivated];
        const uniqueExpired = allExpired.filter((license, index, self) =>
          index === self.findIndex(l => l.id === license.id)
        );

        setExpiredLicenses(uniqueExpired);

        // Update count in notifications store
        setExpiredLicensesCount(uniqueExpired.length);

        // Auto-open on first load if there are expired licenses (only once per org)
        if (!hasAutoOpened && uniqueExpired.length > 0) {
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      } catch (error) {
        console.error('Error checking expired licenses:', error);
      } finally {
        setLoading(false);
      }
    };

    checkExpiredLicenses();
  }, [currentOrgId, firebase, hasAutoOpened, setExpiredLicensesCount, setIsOpen]);

  const handleAcknowledge = async (license: ExpiredLicense) => {
    if (!firebase) return;

    setAcknowledgingIds((prev) => new Set(prev).add(license.id));

    try {
      const success = await firebase.acknowledgeExpiredLicense(
        license.sourceOrgId,
        license.trackId,
        license.licenseId
      );

      if (success) {
        // Remove from list and update count
        setExpiredLicenses((prev) => {
          const updated = prev.filter((l) => l.id !== license.id);
          setExpiredLicensesCount(updated.length);
          return updated;
        });
      }
    } catch (error) {
      console.error('Error acknowledging expired license:', error);
    } finally {
      setAcknowledgingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(license.id);
        return newSet;
      });
    }
  };

  const handleAcknowledgeAll = async () => {
    if (!firebase) return;

    setAcknowledgingAll(true);

    try {
      const results = await Promise.all(
        expiredLicenses.map((license) =>
          firebase.acknowledgeExpiredLicense(
            license.sourceOrgId,
            license.trackId,
            license.licenseId
          )
        )
      );

      // Remove successfully acknowledged licenses
      const successfulAcks = expiredLicenses.filter((_, index) => results[index]);
      if (successfulAcks.length > 0) {
        setExpiredLicenses((prev) => {
          const updated = prev.filter((l) => !successfulAcks.some((s) => s.id === l.id));
          setExpiredLicensesCount(updated.length);
          return updated;
        });
      }
    } catch (error) {
      console.error('Error acknowledging all licenses:', error);
    } finally {
      setAcknowledgingAll(false);
    }
  };

  const handleRemindLater = () => {
    setIsOpen(false);
  };

  // Close modal when all items are processed
  useEffect(() => {
    if (isOpen && expiredLicenses.length === 0 && !loading) {
      setIsOpen(false);
    }
  }, [expiredLicenses.length, isOpen, loading, setIsOpen]);

  const formatLicenseType = (type: string) => {
    switch (type) {
      case 'blanket':
        return 'Blanket License';
      case '1year':
        return '1 Year';
      case '2year':
        return '2 Years';
      case '3year':
        return '3 Years';
      case 'custom':
        return 'Custom Duration';
      default:
        return type;
    }
  };

  const getExpiryInfo = (license: ExpiredLicense) => {
    if (license.deactivatedAt) {
      return `Revoked on ${moment(license.deactivatedAt).format('DD/MM/YYYY')}`;
    }
    if (license.dateExpires) {
      return `Expired on ${moment(license.dateExpires).format('DD/MM/YYYY')}`;
    }
    return 'Expired';
  };

  if (!isOpen || expiredLicenses.length === 0) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleRemindLater}
      style={{
        color: '#ffffff',
        maxWidth: '600px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleRemindLater}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={handleRemindLater}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          License Expiration Notice ({expiredLicenses.length})
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="light" />
          </div>
        ) : (
          <>
            <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
              The following licenses have expired or been revoked. You can request a renewal from the track's license history if needed.
            </p>
            <div className="expired-licenses-list">
              {expiredLicenses.map((license) => (
                <div
                  key={license.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.75rem 1rem',
                    marginBottom: '0.5rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 500,
                        marginBottom: '0.25rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {license.trackName || 'Unknown Track'}
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'rgba(255, 255, 255, 0.5)',
                      }}
                    >
                      {license.sourceOrgName && `From: ${license.sourceOrgName} | `}
                      {formatLicenseType(license.licenseType)} | {getExpiryInfo(license)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="btn-secondary-custom ml-3"
                    onClick={() => handleAcknowledge(license)}
                    disabled={acknowledgingIds.has(license.id) || acknowledgingAll}
                    style={{ minWidth: '80px' }}
                  >
                    {acknowledgingIds.has(license.id) ? (
                      <Spinner size="sm" />
                    ) : (
                      'Dismiss'
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Button
          className="btn-secondary-custom"
          onClick={handleRemindLater}
          disabled={acknowledgingAll}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Remind Me Later</span>
        </Button>
        <Button
          className="btn-primary-custom"
          onClick={handleAcknowledgeAll}
          disabled={acknowledgingAll || expiredLicenses.length === 0}
        >
          {acknowledgingAll ? (
            <>
              <Spinner size="sm" className="mr-2" /> Dismissing...
            </>
          ) : (
            <span style={{ position: 'relative', zIndex: 2 }}>
              Dismiss All ({expiredLicenses.length})
            </span>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExpiredLicensesModal;
